import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { hablar } from '../utils/audioHelper'

const API = process.env.REACT_APP_API_URL

/* Reproduce la pronunciacion real si hay audio_url, si no usa la
   voz sintetica del navegador como respaldo. Avisa por medio de
   onCargando(true/false) mientras el audio esta descargando/almacenando
   en buffer, para que el boton no se sienta "colgado" si el archivo
   (que viene de un servidor externo) tarda un poco en cargar. */
function reproducir(entrada, onCargando) {
  if (entrada?.audio_url) {
    onCargando(true)
    const audio = new Audio(entrada.audio_url)
    const terminarCarga = () => onCargando(false)
    audio.addEventListener('playing', terminarCarga, { once: true })
    audio.addEventListener('error', terminarCarga, { once: true })
    audio.play().catch(() => { terminarCarga(); hablar(entrada.palabra) })
  } else if (entrada?.palabra) {
    hablar(entrada.palabra)
  }
}

/* Tarjeta de resultado (se usa tanto en la busqueda como en la
   lista de palabras guardadas). */
function TarjetaPalabra({ entrada, guardada, onGuardar, onQuitar, cargando }) {
  const [cargandoAudio, setCargandoAudio] = useState(false)
  return (
    <div style={{ background: '#fff', border: '1px solid var(--gris-borde)', borderRadius: 16, padding: 22 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: 'var(--azul-oscuro)' }}>{entrada.palabra}</h2>
          <button onClick={() => reproducir(entrada, setCargandoAudio)}
            disabled={cargandoAudio}
            style={{ background: '#E8EFFE', border: 'none', borderRadius: '50%', width: 30, height: 30, cursor: cargandoAudio ? 'default' : 'pointer', fontSize: 14, flexShrink: 0, opacity: cargandoAudio ? 0.6 : 1 }}>
            {cargandoAudio ? '⏳' : '🔊'}
          </button>
        </div>
        <button
          onClick={() => guardada ? onQuitar(entrada.palabra) : onGuardar(entrada.palabra)}
          disabled={cargando}
          style={{
            padding: '7px 14px', borderRadius: 999, border: '1.5px solid ' + (guardada ? '#EF4444' : 'var(--dorado)'),
            background: guardada ? '#FEE2E2' : 'var(--dorado)', color: guardada ? '#B91C1C' : '#fff',
            fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700, cursor: cargando ? 'default' : 'pointer', flexShrink: 0, whiteSpace: 'nowrap'
          }}>
          {guardada ? '✕ Quitar' : '★ Guardar'}
        </button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {entrada.fonetica && <span style={{ fontSize: 13, color: 'var(--gris-texto)', fontStyle: 'italic' }}>{entrada.fonetica}</span>}
        {entrada.tipo_gramatical && (
          <span style={{ background: '#F0F4FF', color: 'var(--azul)', fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 999 }}>
            {entrada.tipo_gramatical}
          </span>
        )}
      </div>

      {entrada.traduccion && (
        <p style={{ fontSize: 16, fontWeight: 700, color: '#059669', marginBottom: 10 }}>{entrada.traduccion}</p>
      )}

      {entrada.definicion && (
        <p style={{ fontSize: 14, color: '#1A1A2E', marginBottom: 12 }}>{entrada.definicion}</p>
      )}

      {entrada.ejemplo && (
        <div style={{ background: '#F8FAFF', borderRadius: 12, padding: 14 }}>
          <p style={{ fontSize: 13.5, color: '#1A1A2E', fontStyle: 'italic' }}>"{entrada.ejemplo}"</p>
          {entrada.ejemplo_traduccion && (
            <p style={{ fontSize: 12, color: 'var(--gris-texto)', marginTop: 4 }}>{entrada.ejemplo_traduccion}</p>
          )}
        </div>
      )}
    </div>
  )
}

/* Pagina de "Diccionario": busqueda en vivo ingles → espanol
   (Free Dictionary API + MyMemory, cacheadas en Supabase, ver
   api/src/routes/diccionario.js) con guardado de palabras. */
export default function Diccionario({ usuario, onCambio }) {
  const [pestana, setPestana] = useState('buscar') // 'buscar' | 'guardadas'

  const [consulta,      setConsulta]      = useState('')
  const [buscando,      setBuscando]      = useState(false)
  const [resultado,     setResultado]     = useState(null)
  const [noEncontrada,  setNoEncontrada]  = useState(false)
  const [errorBusqueda, setErrorBusqueda] = useState(null)
  const [accionando,    setAccionando]    = useState(false)

  const [guardadas,        setGuardadas]        = useState([])
  const [cargandoGuardadas, setCargandoGuardadas] = useState(true)

  const palabrasGuardadasSet = new Set(guardadas.map(g => g.palabra))

  const cargarGuardadas = useCallback(() => {
    if (!usuario?.id) { setCargandoGuardadas(false); return }
    setCargandoGuardadas(true)
    axios.get(`${API}/diccionario/guardadas/${usuario.id}`)
      .then(res => setGuardadas(res.data.datos || []))
      .catch(err => console.error('Error cargando palabras guardadas:', err))
      .finally(() => setCargandoGuardadas(false))
  }, [usuario])

  useEffect(() => { cargarGuardadas() }, [cargarGuardadas])

  function buscar(e) {
    e.preventDefault()
    const termino = consulta.trim()
    if (!termino || buscando) return
    setBuscando(true)
    setErrorBusqueda(null)
    setNoEncontrada(false)
    setResultado(null)
    axios.get(`${API}/diccionario/buscar/${encodeURIComponent(termino)}`).then(res => {
      if (res.data.encontrada) setResultado(res.data.datos)
      else setNoEncontrada(true)
    }).catch(err => {
      console.error('Error buscando palabra:', err)
      setErrorBusqueda('No pudimos buscar esa palabra. Intenta de nuevo.')
    }).finally(() => setBuscando(false))
  }

  async function guardarPalabra(palabra) {
    if (!usuario?.id || accionando) return
    setAccionando(true)
    try {
      await axios.post(`${API}/diccionario/guardar`, { usuario_id: usuario.id, palabra })
      cargarGuardadas()
      if (onCambio) onCambio()
    } catch (err) {
      console.error('Error guardando palabra:', err)
    } finally {
      setAccionando(false)
    }
  }

  async function quitarPalabra(palabra) {
    if (!usuario?.id || accionando) return
    setAccionando(true)
    try {
      await axios.delete(`${API}/diccionario/guardar`, { data: { usuario_id: usuario.id, palabra } })
      cargarGuardadas()
      if (onCambio) onCambio()
    } catch (err) {
      console.error('Error quitando palabra:', err)
    } finally {
      setAccionando(false)
    }
  }

  return (
    <div style={{ animation: 'fadeIn .3s ease both', maxWidth: 640 }}>
      <div style={{ marginBottom: 22 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800 }}>🔍 Diccionario</h1>
        <p style={{ color: 'var(--gris-texto)', fontSize: 14, marginTop: 4 }}>
          Busca cualquier palabra en ingles y guarda las que quieras repasar despues.
        </p>
      </div>

      <div style={{ display: 'flex', background: '#F0F2F8', border: '1px solid var(--gris-borde)', borderRadius: 10, overflow: 'hidden', marginBottom: 20, maxWidth: 360 }}>
        <button onClick={() => setPestana('buscar')}
          style={{ flex: 1, padding: '9px 0', border: 'none', background: pestana === 'buscar' ? 'var(--azul)' : 'transparent',
            color: pestana === 'buscar' ? '#fff' : 'var(--gris-texto)', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          Buscar
        </button>
        <button onClick={() => setPestana('guardadas')}
          style={{ flex: 1, padding: '9px 0', border: 'none', background: pestana === 'guardadas' ? 'var(--azul)' : 'transparent',
            color: pestana === 'guardadas' ? '#fff' : 'var(--gris-texto)', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          Guardadas ({guardadas.length})
        </button>
      </div>

      {pestana === 'buscar' && (
        <div>
          <form onSubmit={buscar} style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
            <input
              value={consulta}
              onChange={e => setConsulta(e.target.value)}
              placeholder="Escribe una palabra en ingles..."
              style={{ flex: 1, padding: '13px 16px', borderRadius: 12, border: '1.5px solid var(--gris-borde)', fontFamily: 'inherit', fontSize: 14, outline: 'none' }}
            />
            <button type="submit" disabled={buscando}
              style={{ padding: '13px 22px', borderRadius: 12, border: 'none', background: 'var(--azul)', color: '#fff',
                fontFamily: 'inherit', fontSize: 14, fontWeight: 700, cursor: buscando ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>
              {buscando ? 'Buscando...' : '🔍 Buscar'}
            </button>
          </form>

          {errorBusqueda && (
            <div style={{ background: '#FEE2E2', color: '#B91C1C', borderRadius: 10, padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>
              {errorBusqueda}
            </div>
          )}

          {noEncontrada && (
            <div style={{ textAlign: 'center', padding: '30px 20px', color: 'var(--gris-texto)' }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>🤔</div>
              <p>No encontramos "<strong>{consulta.trim()}</strong>" en el diccionario. Revisa la ortografia o intenta con otra palabra.</p>
            </div>
          )}

          {resultado && (
            <TarjetaPalabra
              entrada={resultado}
              guardada={palabrasGuardadasSet.has(resultado.palabra)}
              onGuardar={guardarPalabra}
              onQuitar={quitarPalabra}
              cargando={accionando}
            />
          )}

          {!resultado && !noEncontrada && !errorBusqueda && (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--gris-texto)' }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>📖</div>
              <p style={{ fontSize: 13 }}>Escribe una palabra en ingles arriba para empezar a buscar.</p>
            </div>
          )}
        </div>
      )}

      {pestana === 'guardadas' && (
        <div>
          {cargandoGuardadas ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--gris-texto)' }}>Cargando...</div>
          ) : guardadas.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--gris-texto)' }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>⭐</div>
              <p style={{ fontSize: 13 }}>Todavia no has guardado ninguna palabra. Busca una y toca "★ Guardar".</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {guardadas.map(entrada => (
                <TarjetaPalabra
                  key={entrada.palabra}
                  entrada={entrada}
                  guardada={true}
                  onGuardar={guardarPalabra}
                  onQuitar={quitarPalabra}
                  cargando={accionando}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
